import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import instance from "api/apiClient";
import { apiRoutes } from "configs/apiRoutes";
import { ModalTypes } from "configs/types";
import { Modal } from "components/Modal/Modal";
import Button from "components/buttons/Button";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import { useAppDispatch, useAppSelector } from "store/index";
import { openModal } from "store/modals/slice";
import {
  getPlayerItem,
  getPaymentInfo,
  handleWithdrawalsClear,
  handleInfoClear,
} from "store/players/list/slice";
import { getCurrencyList } from "store/settings/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import { handleApiError } from "helpers/handleApiError";
import { usePermissions } from "helpers/usePermission";
import styles from "./FinanceActionModal.module.scss";

type Method = { id: number; title: string; currencyType?: string };
type CashierField = { code: string; title: string };
type PaymentMethodLimits = {
  minAmount?: number | string | null;
  maxAmount?: number | string | null;
};
type CurrencyRate = {
  currencyFrom: string;
  rate: number | string | null;
  status?: string;
};

const DECIMAL_PATTERN = /^\d+(?:\.\d+)?$/;

const CreateWithdrawal: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const userId = Number(params.get("entityId"));
  const player = useAppSelector((state) => state.players.payload);
  const currencies = useAppSelector((state) => state.settings.currencies);
  const permissions = usePermissions("payments_transactions_withdrawals");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("");
  const [currencyRates, setCurrencyRates] = useState<CurrencyRate[]>([]);
  const [paymentMethodId, setMethod] = useState<string | number>("");
  const [reason, setReason] = useState("");
  const [methods, setMethods] = useState<Method[]>([]);
  const [fields, setFields] = useState<CashierField[]>([]);
  const [limits, setLimits] = useState<PaymentMethodLimits>({});
  const [values, setValues] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [loading, setLoading] = useState(false);
  const [configLoading, setConfigLoading] = useState(false);
  const [configReady, setConfigReady] = useState(false);
  const [isConfirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const referenceStorageKey = `manualWithdrawalAction:${userId}`;
  const [reference, setReference] = useState("");
  const isPoker = [
    "bo.planets.casino",
    "backoffice-staging.planets.casino",
  ].includes(window.location.hostname);
  const currencyOptions = useMemo(
    () => currencies.filter((code) => !isPoker || code === "USD"),
    [currencies, isPoker],
  );
  const conversionCurrencies = useMemo(
    () => currencies.filter((code) => code !== currency),
    [currencies, currency],
  );
  const convertedAmounts = useMemo(() => {
    const enteredAmount = Number(amount);
    if (!Number.isFinite(enteredAmount) || enteredAmount <= 0) return [];

    return conversionCurrencies.flatMap((conversionCurrency) => {
      const rate = currencyRates.find(
        (item) => item.currencyFrom === conversionCurrency,
      );
      const numericRate = Number(rate?.rate);

      if (
        rate?.status !== "available" ||
        !Number.isFinite(numericRate) ||
        numericRate <= 0
      ) {
        return [];
      }

      return [
        {
          currency: conversionCurrency,
          amount: (enteredAmount / numericRate).toLocaleString(undefined, {
            maximumFractionDigits: 2,
          }),
        },
      ];
    });
  }, [amount, conversionCurrencies, currencyRates]);

  useEffect(() => {
    sessionStorage.removeItem(referenceStorageKey);
  }, [referenceStorageKey]);

  useEffect(() => {
    dispatch(getPlayerItem(userId));
    dispatch(getCurrencyList());
  }, [dispatch, userId]);
  useEffect(() => {
    if (!currency || !conversionCurrencies.length) {
      setCurrencyRates([]);
      return;
    }

    const controller = new AbortController();
    setCurrencyRates([]);
    instance
      .post(
        apiRoutes.paymentCurrenciesRatesMap,
        {
          currencyFrom: conversionCurrencies,
          baseCurrency: currency,
          includeMissing: 1,
        },
        { signal: controller.signal },
      )
      .then((result) => {
        if (controller.signal.aborted) return;
        const data = result?.data;
        const items = Array.isArray(data)
          ? data
          : (data?.items ?? data?.rates ?? []);
        setCurrencyRates(Array.isArray(items) ? items : []);
      })
      .catch(() => {
        if (!controller.signal.aborted) setCurrencyRates([]);
      });

    return () => controller.abort();
  }, [currency, conversionCurrencies]);
  useEffect(() => {
    if (!currency) return;
    const controller = new AbortController();
    setLoading(true);
    setMethod("");
    setMethods([]);
    instance
      .post(
        apiRoutes.paymentMethodList,
        {
          limit: 500,
          offset: 0,
          transactionType: "withdrawal",
          currency: [currency],
          configIsActive: 1,
        },
        { signal: controller.signal },
      )
      .then((result) => {
        if (!controller.signal.aborted) setMethods(result.data?.items ?? []);
      })
      .catch((error) => {
        if (!controller.signal.aborted) handleApiError({ error, t, dispatch });
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [currency, t, dispatch]);
  useEffect(() => {
    setFields([]);
    setValues({});
    setLimits({});
    setConfigReady(false);
    if (!paymentMethodId || !currency) return;
    const controller = new AbortController();
    setConfigLoading(true);
    const country = player?.country || "WW";
    const load = async () => {
      let result = await instance.get(
        `${apiRoutes.paymentMethodGetConfig}/${paymentMethodId}/${country}/${currency}`,
        { signal: controller.signal },
      );
      if (!result.data?.isActive && country !== "WW")
        result = await instance.get(
          `${apiRoutes.paymentMethodGetConfig}/${paymentMethodId}/WW/${currency}`,
          { signal: controller.signal },
        );
      if (!controller.signal.aborted) {
        setFields(result.data?.cashierFields ?? []);
        setLimits({
          minAmount: result.data?.minAmount,
          maxAmount: result.data?.maxAmount,
        });
        setConfigReady(Boolean(result.data?.isActive));
      }
    };
    load()
      .catch((error) => {
        if (!controller.signal.aborted) handleApiError({ error, t, dispatch });
      })
      .finally(() => {
        if (!controller.signal.aborted) setConfigLoading(false);
      });
    return () => controller.abort();
  }, [paymentMethodId, currency, player?.country, dispatch, t]);

  const close = () => {
    if (inFlight.current) return;
    sessionStorage.removeItem(referenceStorageKey);
    setReference("");
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.player}&entityId=${userId}&tab=finance`,
    );
    dispatch(openModal({ activeModal: ModalTypes.player }));
  };
  const submit = async () => {
    if (inFlight.current || !permissions?.includes("update")) return;
    inFlight.current = true;
    setBusy(true);
    setErrors({});
    try {
      let actionReference = reference;
      if (!actionReference) {
        const result = await instance.post(
          apiRoutes.paymentManualWithdrawalPrepare,
          {
            userId,
            amount,
            currency,
            paymentMethodId: Number(paymentMethodId),
            reason,
            ...values,
          },
        );
        actionReference = result.data.actionReference;
        sessionStorage.setItem(referenceStorageKey, actionReference);
        setReference(actionReference);
      }
      const result = await instance.post(
        apiRoutes.paymentManualWithdrawalExecute,
        { userId, actionReference },
      );
      sessionStorage.removeItem(referenceStorageKey);
      const failed = ["failed", "declined", "canceled"].includes(
        result.data.status,
      );
      dispatch(
        showNotification({
          title: t(failed ? "error" : "success"),
          message: t(
            failed ? "manualWithdrawalRejected" : "manualWithdrawalCreated",
          ),
          type: failed ? NotificationTypes.error : NotificationTypes.success,
        }),
      );
      dispatch(handleWithdrawalsClear());
      dispatch(handleInfoClear());
      await Promise.all([
        dispatch(getPlayerItem(userId)),
        dispatch(getPaymentInfo(String(userId))),
      ]);
      inFlight.current = false;
      close();
    } catch (error) {
      const hasServerResponse =
        typeof error === "object" &&
        error !== null &&
        "response" in error &&
        Boolean((error as { response?: unknown }).response);

      if (hasServerResponse) {
        sessionStorage.removeItem(referenceStorageKey);
        setReference("");
        setConfirming(false);
      }

      handleApiError({
        error,
        t,
        dispatch,
        formErrors: {
          setFieldError: (field, code) =>
            setErrors((current) => ({ ...current, [field]: code })),
        },
      });
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };
  const selectedPaymentMethodLabel =
    methods.find((method) => String(method.id) === String(paymentMethodId))
      ?.title || String(paymentMethodId);
  const clearError = (field: string) => {
    setErrors((current) => ({ ...current, [field]: undefined }));
  };
  const warnAboutAmount = (message: string) => {
    dispatch(
      showNotification({
        title: t("warning"),
        message,
        type: NotificationTypes.warning,
      }),
    );
  };
  const validate = () => {
    const nextErrors: Record<string, string | undefined> = {};
    const normalizedReason = reason.trim();
    const numericAmount = Number(amount);
    const minAmount =
      limits.minAmount == null ? undefined : Number(limits.minAmount);
    const maxAmount =
      limits.maxAmount == null ? undefined : Number(limits.maxAmount);
    const availableBalance =
      player?.balanceReal == null ? undefined : Number(player.balanceReal);

    if (!amount) nextErrors.amount = "requiredField";
    else if (!DECIMAL_PATTERN.test(amount) || numericAmount <= 0) {
      nextErrors.amount = "invalidAmount";
    } else if (Number.isFinite(minAmount) && numericAmount < minAmount!) {
      nextErrors.amount = "amountBelowMinimum";
      warnAboutAmount(
        t("manualWithdrawalAmountBelowMinimum", {
          amount: minAmount,
          currency,
        }),
      );
    } else if (Number.isFinite(maxAmount) && numericAmount > maxAmount!) {
      nextErrors.amount = "amountAboveMaximum";
      warnAboutAmount(
        t("manualWithdrawalAmountAboveMaximum", {
          amount: maxAmount,
          currency,
        }),
      );
    } else if (
      currency === player?.currency &&
      Number.isFinite(availableBalance) &&
      numericAmount > availableBalance!
    ) {
      nextErrors.amount = "insufficientBalance";
      warnAboutAmount(t("manualWithdrawalInsufficientBalance"));
    }
    if (!currency) nextErrors.currency = "requiredField";
    if (!paymentMethodId) nextErrors.paymentMethodId = "requiredField";
    if (!normalizedReason) nextErrors.reason = "requiredField";
    else if (normalizedReason.length > 255) {
      nextErrors.reason = "reasonMaxLength";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };
  const handleRequestConfirmation = () => {
    if (validate() && configReady) setConfirming(true);
  };
  const handleBackToForm = () => {
    sessionStorage.removeItem(referenceStorageKey);
    setReference("");
    setConfirming(false);
  };
  const field = (
    key: string,
    label: string,
    value: string,
    change: (v: string) => void,
    type = "text",
  ) => (
    <div key={key}>
      <InnerInput
        id={`manualWithdrawal-${key}`}
        type={type}
        label={label}
        value={value}
        onChange={change}
        disabled={busy || Boolean(reference)}
        hasError={Boolean(errors[key])}
      />
      {errors[key] && <span role="alert">{t(`errors.${errors[key]}`)}</span>}
    </div>
  );
  return (
    <Modal onClose={close}>
      <Modal.Header className={styles.modalHeader}>
        <p>{t("manualWithdrawal")}</p>
      </Modal.Header>
      <Modal.Content className={styles.modalContent}>
        {isConfirming ? (
          <div className={styles.confirmation}>
            <h3>{t("confirmManualWithdrawal")}</h3>
            <p>{t("manualWithdrawalConfirmation")}</p>
            <dl>
              <div>
                <dt>{t("amount")}</dt>
                <dd>
                  {amount} {currency}
                </dd>
              </div>
              <div>
                <dt>{t("currency")}</dt>
                <dd>{currency}</dd>
              </div>
              <div>
                <dt>{t("paymentMethod")}</dt>
                <dd>{selectedPaymentMethodLabel}</dd>
              </div>
              {fields.map((item) =>
                values[item.code] ? (
                  <div key={item.code}>
                    <dt>{t(item.code, { defaultValue: item.title })}</dt>
                    <dd>{values[item.code]}</dd>
                  </div>
                ) : null,
              )}
              <div>
                <dt>{t("reason")}</dt>
                <dd>{reason.trim()}</dd>
              </div>
            </dl>
          </div>
        ) : (
          <div className={styles.form}>
            <>
              <div className={styles.withdrawalAmountRow}>
                <InnerInput
                  type="text"
                  label={t("availableBalance")}
                  value={
                    player?.selectedRealBalance != null &&
                    player.selectedRealBalanceCurrency
                      ? `${player.selectedRealBalance} ${player.selectedRealBalanceCurrency}`
                      : player
                        ? `${player.balanceReal ?? 0}${
                            player.currency ? ` ${player.currency}` : ""
                          }`
                        : "—"
                  }
                  onChange={() => undefined}
                  id="manualWithdrawal-availableBalance"
                  disabled
                />
                <div>
                  <InnerInput
                    type="text"
                    label={t("amount")}
                    placeholder={t("enterAmount")}
                    value={amount}
                    onChange={(value) => {
                      setAmount(value);
                      clearError("amount");
                    }}
                    id="manualWithdrawal-amount"
                    inputMode="decimal"
                    isRequired
                    tooltipText={t("manualWithdrawalAmountTooltip")}
                    tooltipPosition="right"
                    disabled={busy}
                    hasError={Boolean(errors.amount)}
                    title={errors.amount}
                  />
                </div>
                <div>
                  <DefaultDropdown
                    label={t("currency")}
                    placeholder={t("chooseCurrency")}
                    value={currency}
                    options={currencyOptions.map((code) => ({
                      value: code,
                      label: code,
                    }))}
                    onSelect={(value) => {
                      setCurrency(String(value));
                      clearError("currency");
                      clearError("paymentMethodId");
                    }}
                    disabled={busy}
                    isRequired
                    hasError={Boolean(errors.currency)}
                    tooltipText={t("manualWithdrawalCurrencyTooltip")}
                    tooltipPosition="right"
                  />
                </div>
              </div>
              {convertedAmounts.length > 0 && (
                <div className={styles.currencyEquivalents} aria-live="polite">
                  {convertedAmounts.map((item) => (
                    <span key={item.currency}>
                      ≈ {item.amount} {item.currency}
                    </span>
                  ))}
                </div>
              )}
              <DefaultDropdown
                label={t("paymentMethod")}
                placeholder={t("chooseMethod")}
                value={paymentMethodId}
                options={methods.map((method) => ({
                  value: method.id,
                  label: method.title,
                }))}
                onSelect={(value) => {
                  setMethod(value);
                  clearError("paymentMethodId");
                }}
                loading={loading || configLoading}
                disabled={busy || loading}
                isRequired
                hasError={Boolean(errors.paymentMethodId)}
                tooltipText={t("manualWithdrawalPaymentMethodTooltip")}
                tooltipPosition="right"
              />
              {configReady &&
                (limits.minAmount != null || limits.maxAmount != null) && (
                  <div
                    className={styles.currencyEquivalents}
                    aria-live="polite"
                  >
                    {limits.minAmount != null && (
                      <span>
                        {t("min")}: {limits.minAmount} {currency}
                      </span>
                    )}
                    {limits.maxAmount != null && (
                      <span>
                        {t("max")}: {limits.maxAmount} {currency}
                      </span>
                    )}
                  </div>
                )}
              {fields.map((item) =>
                field(
                  item.code,
                  t(item.code, { defaultValue: item.title }),
                  values[item.code] || "",
                  (value) =>
                    setValues((current) => ({
                      ...current,
                      [item.code]: value,
                    })),
                  item.code === "cardNumber" ? "password" : "text",
                ),
              )}
              <InnerInput
                type="textarea"
                label={t("reason")}
                placeholder={t("enterManualWithdrawalReason")}
                value={reason}
                onChange={(value) => {
                  setReason(value);
                  clearError("reason");
                }}
                id="manualWithdrawal-reason"
                rows={7}
                isRequired
                tooltipText={t("manualWithdrawalReasonTooltip")}
                tooltipPosition="right"
                disabled={busy}
                hasError={Boolean(errors.reason)}
                title={errors.reason}
              />
            </>
          </div>
        )}
      </Modal.Content>
      <Modal.Footer renderCustomFooter>
        {isConfirming ? (
          <>
            <Button
              isSecondary
              txt={t("back")}
              handleClick={handleBackToForm}
              disabled={busy}
            />
            <Button
              isGreen
              txt={t("confirm")}
              handleClick={submit}
              isLoad={busy}
              disabled={!permissions?.includes("update")}
            />
          </>
        ) : (
          <>
            <Button txt={t("cancel")} handleClick={close} disabled={busy} />
            <Button
              isGreen
              txt={t("create")}
              handleClick={handleRequestConfirmation}
              disabled={!permissions?.includes("update")}
            />
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};
export default CreateWithdrawal;
